/**
 * What a former employee can still reach, and until when (PAYD-FR-042,
 * payroll depth H-06).
 *
 * An alumnus is not a member with fewer permissions. They have no workspace,
 * no colleagues and no navigation: the shore refuses an alumni session on
 * every route that is not tagged `@AlumniAllowed()`, and answers
 * `ALUMNI_ACCESS_EXPIRED` once the window closes. This module is the one
 * place that decides which of those situations somebody is in, so no
 * component has to guess and none of them can imply access that is not there.
 *
 * Pure: `now` is always a parameter, never a clock read.
 */

/// How close to the end counts as "ending soon". Two weeks is long enough to
/// download a settlement statement and ask HR for anything missing, which is
/// the whole reason to warn rather than let it lapse silently.
export const ACCESS_ENDING_SOON_DAYS = 14;

const MS_PER_DAY = 86_400_000;

export interface AlumniBinding {
  workspaceId: string;
  accessUntil: string;
}

export type AlumniAccess =
  /// Not an alumnus at all: nothing to show, and nothing to hint at.
  | { state: "none" }
  | { state: "open"; accessUntil: string; daysLeft: number }
  | { state: "ending"; accessUntil: string; daysLeft: number }
  | { state: "expired"; accessUntil: string };

/**
 * The one window the landing page reads.
 *
 * When somebody has left more than one workspace, the furthest date wins. The
 * shore sends no workspace NAME with an alumni binding (there is nothing to
 * name them by), so per-workspace windows would render as "Employer 1" and
 * "Employer 2", which tells the reader less than one honest date does. The
 * shore still enforces each window separately; this only decides what the
 * banner says.
 */
export function resolveAlumniAccess(
  bindings: ReadonlyArray<AlumniBinding>,
  now: Date,
): AlumniAccess {
  let furthest: number | null = null;
  let furthestIso = "";
  for (const binding of bindings) {
    const at = Date.parse(binding.accessUntil);
    if (Number.isNaN(at)) continue;
    if (furthest === null || at > furthest) {
      furthest = at;
      furthestIso = binding.accessUntil;
    }
  }
  if (furthest === null) return { state: "none" };

  const remaining = furthest - now.getTime();
  if (remaining <= 0) return { state: "expired", accessUntil: furthestIso };

  const daysLeft = Math.ceil(remaining / MS_PER_DAY);
  return {
    state: daysLeft <= ACCESS_ENDING_SOON_DAYS ? "ending" : "open",
    accessUntil: furthestIso,
    daysLeft,
  };
}

/**
 * Whether this account has nowhere to go but the alumni page.
 *
 * Somebody who still works somewhere is a member first: they get the product,
 * and the alumni page stays reachable by its own URL. Only an account with no
 * live workspace at all gets sent there, because the alternative today is a
 * screen telling a former employee to create a workspace.
 */
export function isAlumniOnly(
  workspaces: ReadonlyArray<unknown>,
  bindings: ReadonlyArray<AlumniBinding>,
): boolean {
  return workspaces.length === 0 && bindings.length > 0;
}
